import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Building2,
  Check,
  GripVertical,
  Inbox,
  Mail,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import ConfirmMessageModal from "../../components/UI/ConfirmMessageModal";
import { companyUseCase, departmentUseCase } from "../../../di/container";
import { httpClient } from "../../../infrastructure/api/http-client";
import { companyStaffApi } from "../../../infrastructure/api/company-staff-api-service";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import { resolveErrorMessage } from "../../../shared/utils/save-flow";
import { useAuthStore } from "../../stores/auth-store";
import type { Department } from "../../../domain/types/department";
import {
  INVITATION_STATUS_LABELS,
  STAFF_ROLE_LABELS,
  type CompanyInvitation,
  type InvitationSendResult,
  type StaffMember,
} from "../../../domain/types/staff";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAIL_NOT_SENT_MESSAGE =
  "초대는 만들었지만 메일을 보내지 못했습니다. 메일 설정을 확인한 뒤 초대 대기 탭에서 다시 보내 주세요.";

/** 왼쪽 부서 목록에서 고른 범위 */
type Scope = { kind: "all" } | { kind: "none" } | { kind: "department"; id: number };
type Tab = "members" | "invitations";

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" }) : "-";

/**
 * 조직 관리 — 예전 "구성원 관리"와 "부서 관리"를 합친 화면.
 *
 * 왼쪽은 부서(조직도), 오른쪽은 고른 부서의 구성원과 대기 중인 초대.
 * - 구성원 = 회사 직원 = 회사 소유자·관리자. 일반 회원(USER)은 쇼핑몰 고객이라 여기 나오지 않는다.
 * - 관리자는 "이메일로 초대"(링크로 로그인·가입) 또는 "회원에서 추가"(쇼핑몰 회원을 바로 관리자로)로 늘린다.
 * - 부서 이동은 구성원 줄에서 바로 고르거나, 부서를 고른 뒤 "부서에 배정"으로 여러 명을 한 번에 옮긴다.
 * 조회는 관리자도 하지만 바꾸는 일은 회사 소유자만 한다(서버도 같은 규칙).
 */
const OrganizationManagement: React.FC = () => {
  const { companyId } = useParams();
  const cid = Number(companyId);
  const isOwner = useAuthStore((state) => state.isCompanyAdmin());

  const [members, setMembers] = useState<StaffMember[]>([]);
  const [invitations, setInvitations] = useState<CompanyInvitation[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<Scope>({ kind: "all" });
  const [tab, setTab] = useState<Tab>("members");
  const [keyword, setKeyword] = useState("");
  const [notice, setNotice] = useState<{ tone: "info" | "error"; text: string } | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"invite" | "promote" | "assign" | null>(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const pendingAction = React.useRef<(() => void) | null>(null);

  const loadPeople = useCallback(async () => {
    if (!Number.isFinite(cid)) return;
    try {
      const [staffList, invitationList] = await Promise.all([
        companyStaffApi.staff(cid),
        companyStaffApi.invitations(cid),
      ]);
      setMembers(staffList);
      setInvitations(invitationList);
    } catch (error) {
      setNotice({ tone: "error", text: resolveErrorMessage(error, "구성원 목록을 불러오지 못했습니다.") });
    } finally {
      setLoading(false);
    }
  }, [cid]);

  const loadDepartments = useCallback(async () => {
    if (!Number.isFinite(cid)) return;
    try {
      setDepartments(await departmentUseCase.getDepartments(cid));
    } catch (error) {
      setNotice({ tone: "error", text: resolveErrorMessage(error, "부서 목록을 불러오지 못했습니다.") });
    }
  }, [cid]);

  useEffect(() => {
    loadPeople();
    loadDepartments();
  }, [loadPeople, loadDepartments]);

  // 선택한 부서가 지워지면 전체로 돌아간다
  useEffect(() => {
    if (scope.kind === "department" && !departments.some((d) => d.id === scope.id)) setScope({ kind: "all" });
  }, [departments, scope]);

  const askConfirm = (message: string, action: () => void) => {
    pendingAction.current = action;
    setConfirmMessage(message);
  };

  const run = async (key: string, action: () => Promise<string | void>) => {
    setBusyKey(key);
    setNotice(null);
    try {
      const done = await action();
      if (done) setNotice({ tone: "info", text: done });
    } catch (error) {
      setNotice({ tone: "error", text: resolveErrorMessage(error, "처리에 실패했습니다.") });
    } finally {
      setBusyKey(null);
      loadPeople();
    }
  };

  // ── 파생 값 ──
  const countOf = (departmentId: number) => members.filter((m) => m.departmentId === departmentId).length;
  const unassignedCount = members.filter((m) => m.departmentId == null).length;
  const openInvitations = invitations.filter((i) => i.status === "PENDING" || i.status === "EXPIRED");
  const selectedDepartment = scope.kind === "department" ? departments.find((d) => d.id === scope.id) : undefined;

  const visibleMembers = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return members.filter((m) => {
      if (scope.kind === "none" && m.departmentId != null) return false;
      if (scope.kind === "department" && m.departmentId !== scope.id) return false;
      if (!q) return true;
      return (m.name ?? "").toLowerCase().includes(q) || (m.email ?? "").toLowerCase().includes(q);
    });
  }, [members, scope, keyword]);


  // ── 구성원 ──
  const changeDepartment = (member: StaffMember, next: number | null) => {
    if (next === member.departmentId) return;
    const label = member.name || member.email || "구성원";
    const apply = () =>
      run(`dept-${member.userId}`, async () => {
        if (next == null) {
          await departmentUseCase.removeMember(cid, member.departmentId!, member.userId);
          return `${label} 님을 부서에서 뺐습니다.`;
        }
        await departmentUseCase.assignMembers(cid, next, [member.userId]);
        return `${label} 님을 ${departments.find((d) => d.id === next)?.name ?? "부서"}(으)로 옮겼습니다.`;
      });
    apply();
  };

  const releaseManager = (member: StaffMember) =>
    askConfirm(
      `${member.name ?? member.email} 님의 회사 관리자 권한을 해제하시겠습니까? 일반 회원이 되어 조직에서 빠지고 부서도 비워집니다.`,
      () =>
        run(`release-${member.userId}`, async () => {
          await companyUseCase.updateUserRole({ companyId: cid, targetUserIds: [member.userId], targetRequestRole: "USER" });
          return "관리자 권한을 해제했습니다.";
        }),
    );

  // ── 초대 ──
  const noticeForSend = (result: InvitationSendResult, done: string) =>
    setNotice(result.mailSent ? { tone: "info", text: done } : { tone: "error", text: MAIL_NOT_SENT_MESSAGE });

  const resend = (invitation: CompanyInvitation) =>
    run(`resend-${invitation.id}`, async () => {
      noticeForSend(await companyStaffApi.resend(cid, invitation.id), `${invitation.email}로 초대 메일을 다시 보냈습니다.`);
    });

  const revoke = (invitation: CompanyInvitation) =>
    askConfirm(`${invitation.email} 초대를 취소하시겠습니까? 보낸 초대 링크는 더 이상 쓸 수 없습니다.`, () =>
      run(`revoke-${invitation.id}`, async () => {
        await companyStaffApi.revoke(cid, invitation.id);
        return "초대를 취소했습니다.";
      }),
    );

  // ── 부서 ──
  const departmentActions: DepartmentActions = {
    create: (name) =>
      run("dept-create", async () => {
        await departmentUseCase.createDepartment(cid, name);
        await loadDepartments();
        return `${name} 부서를 만들었습니다.`;
      }),
    rename: (department, name) =>
      run(`dept-rename-${department.id}`, async () => {
        await departmentUseCase.updateDepartment(cid, department.id, name);
        await loadDepartments();
      }),
    remove: (department) => {
      const count = countOf(department.id);
      if (count > 0) {
        setNotice({ tone: "error", text: `${department.name}에 구성원 ${count}명이 있어 삭제할 수 없습니다. 먼저 다른 부서로 옮겨 주세요.` });
        return;
      }
      askConfirm(`${department.name} 부서를 삭제하시겠습니까?`, () =>
        run(`dept-delete-${department.id}`, async () => {
          await departmentUseCase.deleteDepartment(cid, department.id);
          await loadDepartments();
          return "부서를 삭제했습니다.";
        }),
      );
    },
    reorder: async (ordered) => {
      setDepartments(ordered);
      try {
        setDepartments(await departmentUseCase.reorderDepartments(cid, ordered.map((d) => d.id)));
      } catch {
        await loadDepartments();
      }
    },
  };

  return (
    <CompanyAdminLayout>
      <div className="p-5 text-left">
        {/* 머리글 */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-[1.375rem] font-semibold text-[#222]">조직 관리</h2>
            <p className="text-[14px] text-[#757575]">
              구성원 <b className="font-semibold text-[#222]">{members.length}</b>명 · 부서{" "}
              <b className="font-semibold text-[#222]">{departments.length}</b>개
              {openInvitations.length > 0 && (
                <>
                  {" "}· 초대 대기 <b className="font-semibold text-[#4285F4]">{openInvitations.length}</b>건
                </>
              )}
            </p>
          </div>
          {isOwner && (
            <div className="flex gap-2">
              <button onClick={() => setDialog("promote")} className={outlineBtn}>
                <UserPlus size={15} />
                회원에서 추가
              </button>
              <button onClick={() => setDialog("invite")} className={primaryBtn}>
                <Mail size={15} />
                이메일로 초대
              </button>
            </div>
          )}
        </div>

        {!isOwner && (
          <Banner tone="info">조직 구성과 권한 변경은 회사 소유자만 할 수 있습니다. 관리자는 조회만 할 수 있습니다.</Banner>
        )}
        {notice && (
          <Banner tone={notice.tone} onClose={() => setNotice(null)}>
            {notice.text}
          </Banner>
        )}

        <div className="flex flex-col lg:flex-row gap-5 items-start">
          {/* 왼쪽: 부서 */}
          <DepartmentPanel
            departments={departments}
            scope={scope}
            totalCount={members.length}
            unassignedCount={unassignedCount}
            countOf={countOf}
            editable={isOwner}
            busyKey={busyKey}
            actions={departmentActions}
            onScope={(next) => {
              setScope(next);
              setTab("members");
            }}
          />

          {/* 오른쪽: 구성원 / 초대 */}
          <section className="flex-1 min-w-0 w-full rounded-[12px] border border-[#E5E5E5] bg-white overflow-hidden">
            <div className="flex items-center gap-1 px-4 border-b border-[#EEEEEE]">
              <TabButton active={tab === "members"} onClick={() => setTab("members")}>
                구성원
                <Count active={tab === "members"}>{visibleMembers.length}</Count>
              </TabButton>
              <TabButton active={tab === "invitations"} onClick={() => setTab("invitations")}>
                초대 대기
                <Count active={tab === "invitations"} highlight={openInvitations.length > 0}>
                  {openInvitations.length}
                </Count>
              </TabButton>
            </div>

            {tab === "members" ? (
              <>
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#F5F5F5]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[14px] font-semibold text-[#222] truncate">
                      {scope.kind === "all" ? "전체 구성원" : scope.kind === "none" ? "부서 미지정" : selectedDepartment?.name}
                    </span>
                  </div>
                  <div className="ml-auto flex items-center gap-2 w-full sm:w-auto">
                    <label className="relative flex-1 sm:w-[220px]">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]" />
                      <input
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        placeholder="이름, 이메일 검색"
                        className="w-full h-[34px] pl-8 pr-3 text-[13px] border border-[#E0E0E0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
                      />
                    </label>
                    {isOwner && selectedDepartment && (
                      <button onClick={() => setDialog("assign")} className={`${outlineBtn} h-[34px] shrink-0`}>
                        <Plus size={14} />
                        부서에 배정
                      </button>
                    )}
                  </div>
                </div>

                <MemberList
                  loading={loading}
                  members={visibleMembers}
                  departments={departments}
                  editable={isOwner}
                  busyKey={busyKey}
                  emptyText={
                    keyword.trim()
                      ? "검색 결과가 없습니다."
                      : scope.kind === "department"
                        ? isOwner
                          ? '이 부서에 구성원이 없습니다. "부서에 배정"으로 구성원을 넣어 보세요.'
                          : "이 부서에 구성원이 없습니다."
                        : "구성원이 없습니다."
                  }
                  onChangeDepartment={changeDepartment}
                  onRelease={releaseManager}
                />
              </>
            ) : (
              <InvitationList
                loading={loading}
                invitations={openInvitations}
                editable={isOwner}
                busyKey={busyKey}
                onResend={resend}
                onRevoke={revoke}
                onInvite={isOwner ? () => setDialog("invite") : undefined}
              />
            )}
          </section>
        </div>
      </div>

      {dialog === "invite" && (
        <InviteDialog
          companyId={cid}
          departments={departments}
          initialDepartmentId={selectedDepartment?.id}
          onClose={() => setDialog(null)}
          onInvited={(result) => {
            setDialog(null);
            setTab("invitations");
            noticeForSend(result, `${result.invitation.email}로 초대 메일을 보냈습니다.`);
            loadPeople();
          }}
        />
      )}

      {dialog === "promote" && (
        <MemberPickerDialog
          mode="promote"
          companyId={cid}
          departments={departments}
          initialDepartmentId={selectedDepartment?.id}
          onClose={() => setDialog(null)}
          onDone={(text) => {
            setDialog(null);
            setNotice({ tone: "info", text });
            loadPeople();
          }}
        />
      )}

      {dialog === "assign" && selectedDepartment && (
        <MemberPickerDialog
          mode="assign"
          companyId={cid}
          departments={departments}
          targetDepartment={selectedDepartment}
          onClose={() => setDialog(null)}
          onDone={(text) => {
            setDialog(null);
            setNotice({ tone: "info", text });
            loadPeople();
          }}
        />
      )}

      {confirmMessage && (
        <ConfirmMessageModal
          message={confirmMessage}
          setMessage={setConfirmMessage}
          submitFunction={() => {
            const action = pendingAction.current;
            pendingAction.current = null;
            setConfirmMessage("");
            action?.();
          }}
        />
      )}
    </CompanyAdminLayout>
  );
};

// ───────────────────────── 공통 ─────────────────────────

const primaryBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 h-[38px] text-[13px] font-medium rounded-[8px] bg-[#4285F4] text-white hover:bg-[#3367D6] disabled:bg-[#D9D9D9] disabled:cursor-not-allowed transition-colors";
const outlineBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 h-[38px] text-[13px] font-medium rounded-[8px] bg-white text-[#333] border border-[#D9D9D9] hover:border-[#4285F4] hover:text-[#4285F4] disabled:opacity-50 transition-colors";
const inputClass =
  "w-full h-[40px] px-3 text-[14px] border border-[#E0E0E0] rounded-[8px] focus:border-[#4285F4] focus:outline-none bg-white";

const Banner: React.FC<{ tone: "info" | "error"; onClose?: () => void; children: React.ReactNode }> = ({
  tone,
  onClose,
  children,
}) => (
  <div
    className={`mb-4 flex items-start gap-3 rounded-[8px] border px-4 py-3 text-[13px] ${
      tone === "error" ? "border-[#F24A4A]/20 bg-[#FFF4F4] text-[#D63939]" : "border-[#4285F4]/20 bg-[#EBF3FF] text-[#3367D6]"
    }`}
  >
    <span className="flex-1">{children}</span>
    {onClose && (
      <button onClick={onClose} aria-label="알림 닫기" className="opacity-60 hover:opacity-100">
        <X size={14} />
      </button>
    )}
  </div>
);

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    onClick={onClick}
    className={`relative flex items-center gap-1.5 px-3 h-[46px] text-[14px] transition-colors ${
      active ? "font-semibold text-[#222]" : "text-[#888] hover:text-[#555]"
    }`}
  >
    {children}
    {active && <span className="absolute left-2 right-2 bottom-0 h-[2px] rounded-full bg-[#4285F4]" />}
  </button>
);

const Count: React.FC<{ active?: boolean; highlight?: boolean; children: React.ReactNode }> = ({
  active,
  highlight,
  children,
}) => (
  <span
    className={`min-w-[20px] px-1.5 rounded-full text-[11px] leading-[18px] text-center tabular-nums ${
      highlight ? "bg-[#4285F4] text-white" : active ? "bg-[#E8EFFF] text-[#4285F4]" : "bg-[#F2F2F2] text-[#888]"
    }`}
  >
    {children}
  </span>
);

const ROLE_BADGE: Record<string, string> = {
  COMPANY_ADMIN: "bg-[#222] text-white",
  COMPANY_MANAGER: "bg-[#E8EFFF] text-[#4285F4]",
};

const RoleBadge: React.FC<{ role: string }> = ({ role }) => (
  <span className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-[4px] text-[11px] font-medium ${ROLE_BADGE[role] ?? "bg-[#F2F2F2] text-[#666]"}`}>
    {STAFF_ROLE_LABELS[role] ?? role}
  </span>
);

const Avatar: React.FC<{ name: string | null; email: string | null }> = ({ name, email }) => (
  <span className="shrink-0 w-9 h-9 rounded-full bg-[#F0F5FF] text-[#4285F4] text-[13px] font-semibold flex items-center justify-center">
    {(name || email || "?").trim().charAt(0).toUpperCase()}
  </span>
);

const EmptyState: React.FC<{ icon: React.ReactNode; text: string; action?: React.ReactNode }> = ({ icon, text, action }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-[13px] text-[#AAAAAA]">
    <span className="text-[#D0D0D0]">{icon}</span>
    <p>{text}</p>
    {action}
  </div>
);

// ───────────────────────── 부서 패널 ─────────────────────────

interface DepartmentActions {
  create: (name: string) => void;
  rename: (department: Department, name: string) => void;
  remove: (department: Department) => void;
  reorder: (ordered: Department[]) => void;
}

const DepartmentPanel: React.FC<{
  departments: Department[];
  scope: Scope;
  totalCount: number;
  unassignedCount: number;
  countOf: (id: number) => number;
  editable: boolean;
  busyKey: string | null;
  actions: DepartmentActions;
  onScope: (scope: Scope) => void;
}> = ({ departments, scope, totalCount, unassignedCount, countOf, editable, busyKey, actions, onScope }) => {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const submitNew = () => {
    const name = newName.trim();
    if (!name) return;
    actions.create(name);
    setNewName("");
    setAdding(false);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = departments.findIndex((d) => d.id === active.id);
    const to = departments.findIndex((d) => d.id === over.id);
    actions.reorder(arrayMove(departments, from, to));
  };

  return (
    <aside className="w-full lg:w-[280px] shrink-0 rounded-[12px] border border-[#E5E5E5] bg-white overflow-hidden">
      <div className="p-2 border-b border-[#EEEEEE]">
        <ScopeRow
          icon={<Users size={15} />}
          label="전체 구성원"
          count={totalCount}
          active={scope.kind === "all"}
          onClick={() => onScope({ kind: "all" })}
        />
        <ScopeRow
          icon={<Inbox size={15} />}
          label="부서 미지정"
          count={unassignedCount}
          active={scope.kind === "none"}
          onClick={() => onScope({ kind: "none" })}
        />
      </div>

      <div className="flex items-center justify-between px-4 pt-3 pb-1.5">
        <span className="text-[12px] font-semibold text-[#888]">부서 {departments.length}</span>
        {editable && !adding && (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-[#4285F4] hover:text-[#3367D6]"
          >
            <Plus size={13} />
            부서 추가
          </button>
        )}
      </div>

      {adding && (
        <div className="mx-2 mb-1 flex items-center gap-1 rounded-[8px] border border-[#4285F4] px-2 h-[38px]">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitNew();
              if (e.key === "Escape") setAdding(false);
            }}
            placeholder="새 부서 이름"
            className="flex-1 min-w-0 text-[13px] outline-none"
          />
          <IconButton label="추가" tone="primary" disabled={!newName.trim() || busyKey === "dept-create"} onClick={submitNew}>
            <Check size={14} />
          </IconButton>
          <IconButton
            label="닫기"
            onClick={() => {
              setAdding(false);
              setNewName("");
            }}
          >
            <X size={14} />
          </IconButton>
        </div>
      )}

      <div className="px-2 pb-2">
        {departments.length === 0 ? (
          <div className="px-3 py-6 text-center text-[12px] text-[#AAAAAA] leading-relaxed">
            아직 부서가 없습니다.
            {editable && (
              <>
                <br />
                "부서 추가"로 팀을 나눠 보세요.
              </>
            )}
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={departments.map((d) => d.id)} strategy={verticalListSortingStrategy}>
              {departments.map((department) => (
                <DepartmentRow
                  key={department.id}
                  department={department}
                  count={countOf(department.id)}
                  active={scope.kind === "department" && scope.id === department.id}
                  editable={editable}
                  busy={busyKey === `dept-rename-${department.id}` || busyKey === `dept-delete-${department.id}`}
                  onSelect={() => onScope({ kind: "department", id: department.id })}
                  onRename={(name) => actions.rename(department, name)}
                  onRemove={() => actions.remove(department)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
        {editable && departments.length > 1 && (
          <p className="px-3 pt-2 text-[11px] text-[#BBBBBB]">왼쪽 손잡이를 끌어 순서를 바꿀 수 있습니다.</p>
        )}
      </div>
    </aside>
  );
};

const ScopeRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ icon, label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-2 px-3 h-[38px] rounded-[8px] text-[13px] transition-colors ${
      active ? "bg-[#F0F5FF] text-[#4285F4] font-semibold" : "text-[#444] hover:bg-[#FAFAFA]"
    }`}
  >
    <span className={active ? "text-[#4285F4]" : "text-[#AAAAAA]"}>{icon}</span>
    <span className="flex-1 text-left">{label}</span>
    <span className={`text-[12px] tabular-nums ${active ? "text-[#4285F4]" : "text-[#AAAAAA]"}`}>{count}</span>
  </button>
);

const IconButton: React.FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "primary" | "danger";
  children: React.ReactNode;
}> = ({ label, onClick, disabled, tone, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-[6px] transition-colors disabled:opacity-40 ${
      tone === "primary"
        ? "text-[#4285F4] hover:bg-[#E8EFFF]"
        : tone === "danger"
          ? "text-[#AAAAAA] hover:text-[#F24A4A] hover:bg-[#FFF1F0]"
          : "text-[#AAAAAA] hover:text-[#555] hover:bg-[#F2F2F2]"
    }`}
  >
    {children}
  </button>
);

const DepartmentRow: React.FC<{
  department: Department;
  count: number;
  active: boolean;
  editable: boolean;
  busy: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
}> = ({ department, count, active, editable, busy, onSelect, onRename, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: department.id,
    disabled: !editable,
  });
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(department.name);

  const save = () => {
    const next = name.trim();
    if (next && next !== department.name) onRename(next);
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onClick={() => !editing && onSelect()}
      className={`group flex items-center gap-1.5 pl-1 pr-1.5 h-[38px] rounded-[8px] cursor-pointer text-[13px] transition-colors ${
        isDragging ? "relative z-10 bg-white shadow-md" : active ? "bg-[#F0F5FF]" : "hover:bg-[#FAFAFA]"
      }`}
    >
      {editable ? (
        <button
          className="shrink-0 w-5 h-7 flex items-center justify-center cursor-grab active:cursor-grabbing text-[#D0D0D0] hover:text-[#999]"
          aria-label={`${department.name} 순서 변경`}
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={14} />
        </button>
      ) : (
        <span className="shrink-0 w-5 flex justify-center text-[#C8C8C8]">
          <Building2 size={13} />
        </span>
      )}

      {editing ? (
        <>
          <input
            autoFocus
            value={name}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") {
                setName(department.name);
                setEditing(false);
              }
            }}
            className="flex-1 min-w-0 h-[28px] px-2 text-[13px] border border-[#4285F4] rounded-[6px] outline-none"
          />
          <IconButton label="저장" tone="primary" disabled={!name.trim()} onClick={save}>
            <Check size={14} />
          </IconButton>
          <IconButton
            label="취소"
            onClick={() => {
              setName(department.name);
              setEditing(false);
            }}
          >
            <X size={14} />
          </IconButton>
        </>
      ) : (
        <>
          <span className={`flex-1 min-w-0 truncate ${active ? "font-semibold text-[#4285F4]" : "text-[#333]"}`}>
            {department.name}
          </span>
          {editable && (
            <span className="hidden group-hover:flex group-focus-within:flex items-center">
              <IconButton label="이름 바꾸기" disabled={busy} onClick={() => setEditing(true)}>
                <Pencil size={12} />
              </IconButton>
              <IconButton label="삭제" tone="danger" disabled={busy} onClick={onRemove}>
                <Trash2 size={12} />
              </IconButton>
            </span>
          )}
          <span
            className={`shrink-0 min-w-[20px] text-right text-[12px] tabular-nums ${
              active ? "text-[#4285F4]" : "text-[#AAAAAA]"
            } ${editable ? "group-hover:hidden group-focus-within:hidden" : ""}`}
          >
            {count}
          </span>
        </>
      )}
    </div>
  );
};

// ───────────────────────── 구성원 목록 ─────────────────────────

const MemberList: React.FC<{
  loading: boolean;
  members: StaffMember[];
  departments: Department[];
  editable: boolean;
  busyKey: string | null;
  emptyText: string;
  onChangeDepartment: (member: StaffMember, next: number | null) => void;
  onRelease: (member: StaffMember) => void;
}> = ({ loading, members, departments, editable, busyKey, emptyText, onChangeDepartment, onRelease }) => {
  if (loading) return <div className="py-16 text-center text-[13px] text-[#999]">불러오는 중...</div>;
  if (members.length === 0) return <EmptyState icon={<Users size={32} strokeWidth={1.3} />} text={emptyText} />;

  return (
    <ul>
      <li className="hidden md:grid grid-cols-[minmax(0,1fr)_96px_170px_96px_88px] items-center gap-3 px-4 h-[36px] bg-[#FAFAFA] text-[12px] text-[#999]">
        <span>이름</span>
        <span>권한</span>
        <span>부서</span>
        <span>등록일</span>
        <span />
      </li>
      {members.map((member) => {
        const isOwnerRow = member.role === "COMPANY_ADMIN";
        return (
          <li
            key={member.userId}
            className="grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_96px_170px_96px_88px] items-center gap-x-3 gap-y-2 px-4 py-3 border-t border-[#F2F2F2] first:border-t-0 hover:bg-[#FCFCFC]"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={member.name} email={member.email} />
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-[#222] truncate">{member.name || "이름 없음"}</div>
                <div className="text-[12px] text-[#999] truncate" title={member.email ?? undefined}>
                  {member.email || "-"}
                </div>
              </div>
            </div>
            <div className="justify-self-end md:justify-self-start">
              <RoleBadge role={member.role} />
            </div>
            <div className="col-span-2 md:col-span-1">
              {editable && !isOwnerRow ? (
                <select
                  value={member.departmentId ?? ""}
                  disabled={busyKey === `dept-${member.userId}`}
                  onChange={(e) => onChangeDepartment(member, e.target.value === "" ? null : Number(e.target.value))}
                  aria-label={`${member.name ?? member.email} 부서`}
                  className="w-full h-[32px] px-2 text-[13px] text-[#333] border border-[#E5E5E5] rounded-[6px] bg-white hover:border-[#BBBBBB] focus:border-[#4285F4] focus:outline-none disabled:opacity-50"
                >
                  <option value="">부서 없음</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-[13px] text-[#555]">{member.departmentName || "-"}</span>
              )}
            </div>
            <span className="hidden md:block text-[12px] text-[#999] tabular-nums">{formatDate(member.createdAt)}</span>
            <div className="hidden md:flex justify-end">
              {editable && member.role === "COMPANY_MANAGER" && (
                <button
                  disabled={busyKey === `release-${member.userId}`}
                  onClick={() => onRelease(member)}
                  className="text-[12px] text-[#999] hover:text-[#F24A4A] disabled:opacity-50"
                >
                  관리자 해제
                </button>
              )}
            </div>
            {/* 좁은 화면: 해제 버튼을 아래 줄로 */}
            {editable && member.role === "COMPANY_MANAGER" && (
              <div className="md:hidden col-span-2 flex justify-end">
                <button
                  disabled={busyKey === `release-${member.userId}`}
                  onClick={() => onRelease(member)}
                  className="text-[12px] text-[#999] hover:text-[#F24A4A] disabled:opacity-50"
                >
                  관리자 해제
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

// ───────────────────────── 초대 목록 ─────────────────────────

const InvitationList: React.FC<{
  loading: boolean;
  invitations: CompanyInvitation[];
  editable: boolean;
  busyKey: string | null;
  onResend: (invitation: CompanyInvitation) => void;
  onRevoke: (invitation: CompanyInvitation) => void;
  onInvite?: () => void;
}> = ({ loading, invitations, editable, busyKey, onResend, onRevoke, onInvite }) => {
  if (loading) return <div className="py-16 text-center text-[13px] text-[#999]">불러오는 중...</div>;
  if (invitations.length === 0)
    return (
      <EmptyState
        icon={<Mail size={32} strokeWidth={1.3} />}
        text="대기 중인 초대가 없습니다."
        action={
          onInvite && (
            <button onClick={onInvite} className={outlineBtn}>
              <Mail size={14} />
              이메일로 초대
            </button>
          )
        }
      />
    );

  return (
    <ul>
      {invitations.map((invitation) => {
        const expired = invitation.status === "EXPIRED";
        return (
          <li
            key={invitation.id}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-t border-[#F2F2F2] first:border-t-0"
          >
            <span className="shrink-0 w-9 h-9 rounded-full border border-dashed border-[#C9D8F5] text-[#8AA9E6] flex items-center justify-center">
              <Mail size={15} />
            </span>
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-medium text-[#222] truncate" title={invitation.email}>
                  {invitation.email}
                </span>
                <span
                  className={`shrink-0 px-1.5 py-[1px] rounded-[4px] text-[11px] font-medium ${
                    expired ? "bg-[#F2F2F2] text-[#888]" : "bg-[#E8EFFF] text-[#4285F4]"
                  }`}
                >
                  {INVITATION_STATUS_LABELS[invitation.status]}
                </span>
              </div>
              <div className="text-[12px] text-[#999]">
                회사 관리자{invitation.departmentName ? ` · ${invitation.departmentName}` : ""} ·{" "}
                {invitation.invitedByName ?? "-"} 님이 보냄 · {expired ? "만료됨" : `${formatDate(invitation.expiresAt)}까지`}
              </div>
            </div>
            {editable && (
              <div className="flex items-center gap-2 ml-auto">
                <button
                  disabled={busyKey === `resend-${invitation.id}`}
                  onClick={() => onResend(invitation)}
                  className="h-[30px] px-3 text-[12px] font-medium rounded-[6px] text-[#4285F4] bg-[#F0F5FF] hover:bg-[#E2ECFF] disabled:opacity-50"
                >
                  {busyKey === `resend-${invitation.id}` ? "보내는 중..." : "다시 보내기"}
                </button>
                <button
                  disabled={busyKey === `revoke-${invitation.id}`}
                  onClick={() => onRevoke(invitation)}
                  className="h-[30px] px-3 text-[12px] rounded-[6px] text-[#999] hover:text-[#F24A4A] hover:bg-[#FFF1F0] disabled:opacity-50"
                >
                  취소
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

// ───────────────────────── 다이얼로그 ─────────────────────────

const DialogFrame: React.FC<{
  title: string;
  description?: React.ReactNode;
  onClose: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, onClose, footer, children }) => (
  <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
      className="w-[480px] max-w-full max-h-[90vh] rounded-[14px] bg-white shadow-xl overflow-hidden flex flex-col text-left"
    >
      <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
        <div>
          <h3 className="text-[17px] font-bold text-[#222]">{title}</h3>
          {description && <p className="mt-1 text-[13px] text-[#777] leading-relaxed">{description}</p>}
        </div>
        <button onClick={onClose} className="text-[#AAAAAA] hover:text-[#555]" aria-label="닫기">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="px-6 pb-5 overflow-y-auto flex flex-col gap-4">{children}</div>
      <div className="px-6 py-4 bg-[#FAFAFA] border-t border-[#F0F0F0] flex justify-end gap-2">{footer}</div>
    </div>
  </div>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[12px] font-semibold text-[#555]">{label}</span>
    {children}
    {hint && <span className="text-[12px] text-[#999]">{hint}</span>}
  </label>
);

const DepartmentSelect: React.FC<{
  departments: Department[];
  value: number | "";
  onChange: (value: number | "") => void;
  disabled?: boolean;
  emptyLabel?: string;
}> = ({ departments, value, onChange, disabled, emptyLabel = "부서 없음" }) => (
  <select
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
    className={inputClass}
  >
    <option value="">{emptyLabel}</option>
    {departments.map((d) => (
      <option key={d.id} value={d.id}>
        {d.name}
      </option>
    ))}
  </select>
);

const InviteDialog: React.FC<{
  companyId: number;
  departments: Department[];
  initialDepartmentId?: number;
  onClose: () => void;
  onInvited: (result: InvitationSendResult) => void;
}> = ({ companyId, departments, initialDepartmentId, onClose, onInvited }) => {
  const [email, setEmail] = useState("");
  const [departmentId, setDepartmentId] = useState<number | "">(initialDepartmentId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError("초대할 이메일을 정확히 입력해 주세요.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onInvited(
        await companyStaffApi.invite(companyId, {
          email: email.trim(),
          departmentId: departmentId === "" ? undefined : departmentId,
        }),
      );
    } catch (e) {
      setError(resolveErrorMessage(e, "초대를 보내지 못했습니다."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogFrame
      title="이메일로 초대"
      description="초대 메일의 링크로 로그인하거나 가입하면 회사 관리자로 합류합니다. 다른 회사에서 쓰던 POD24 계정이어도 됩니다."
      onClose={onClose}
      footer={
        <>
          <button disabled={busy} onClick={onClose} className={outlineBtn}>
            취소
          </button>
          <button disabled={busy} onClick={submit} className={primaryBtn}>
            {busy ? "보내는 중..." : "초대 보내기"}
          </button>
        </>
      }
    >
      {error && <Banner tone="error">{error}</Banner>}
      <Field label="이메일">
        <input
          type="email"
          value={email}
          autoFocus
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="manager@example.com"
          className={inputClass}
        />
      </Field>
      <Field label="부서 (선택)" hint="초대 링크는 7일 동안 쓸 수 있습니다.">
        <DepartmentSelect departments={departments} value={departmentId} onChange={setDepartmentId} disabled={busy} />
      </Field>
    </DialogFrame>
  );
};

interface MemberCandidate {
  id: number;
  name: string | null;
  email: string | null;
  role: string;
  departmentId: number | null;
  departmentName: string | null;
}

const PICKER_PAGE_SIZE = 30;

/**
 * 회원 고르기.
 * - promote: 쇼핑몰 회원(USER)을 회사 관리자로 지정 (기존 권한 변경 API)
 * - assign: 다른 부서(또는 부서 없음)의 회사 관리자를 고른 부서로 옮긴다. 일반 회원은 서버가 거절한다
 */
const MemberPickerDialog: React.FC<
  {
    companyId: number;
    departments: Department[];
    onClose: () => void;
    onDone: (message: string) => void;
  } & (
    | { mode: "promote"; initialDepartmentId?: number; targetDepartment?: undefined }
    | { mode: "assign"; targetDepartment: Department; initialDepartmentId?: undefined }
  )
> = ({ mode, companyId, departments, targetDepartment, initialDepartmentId, onClose, onDone }) => {
  const [keywordInput, setKeywordInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Map<number, MemberCandidate>>(new Map());
  const [departmentId, setDepartmentId] = useState<number | "">(initialDepartmentId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setSearching(true);
    httpClient
      .get<{ data: { items: MemberCandidate[] } }>(NEW_API_ENDPOINTS.companyController.GET.users(companyId), {
        params: {
          role: mode === "promote" ? "USER" : "COMPANY_MANAGER",
          keyword: keyword || undefined,
          page: 0,
          size: PICKER_PAGE_SIZE,
        },
      })
      .then((response) => {
        if (!alive) return;
        const items = response.data.data?.items ?? [];
        setCandidates(
          mode === "assign"
            ? items.filter((m) => m.departmentId !== targetDepartment?.id)
            : items,
        );
      })
      .catch((e) => alive && setError(resolveErrorMessage(e, "회원을 검색하지 못했습니다.")))
      .finally(() => alive && setSearching(false));
    return () => {
      alive = false;
    };
  }, [companyId, keyword, mode, targetDepartment?.id]);

  const toggle = (member: MemberCandidate) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(member.id)) next.delete(member.id);
      else next.set(member.id, member);
      return next;
    });

  const submit = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    const ids = Array.from(selected.keys());
    try {
      if (mode === "promote") {
        await companyUseCase.updateUserRole({
          companyId,
          targetUserIds: ids,
          targetRequestRole: "COMPANY_MANAGER",
          departmentId: departmentId === "" ? undefined : departmentId,
        });
        onDone(`${ids.length}명을 회사 관리자로 지정했습니다.`);
      } else {
        await departmentUseCase.assignMembers(companyId, targetDepartment.id, ids);
        onDone(`${ids.length}명을 ${targetDepartment.name}에 배정했습니다.`);
      }
    } catch (e) {
      setError(resolveErrorMessage(e, mode === "promote" ? "관리자로 지정하지 못했습니다." : "부서에 배정하지 못했습니다."));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "promote" ? "회원에서 추가" : `${targetDepartment?.name}에 배정`;
  const description =
    mode === "promote"
      ? "이 쇼핑몰에 가입한 회원을 회사 관리자로 지정합니다. 지정하면 MES 회사 관리 화면에 들어올 수 있습니다."
      : "회사 관리자를 이 부서로 옮깁니다. 새 직원은 먼저 이메일로 초대하거나 회원에서 추가해 주세요.";

  return (
    <DialogFrame
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          <button disabled={busy} onClick={onClose} className={outlineBtn}>
            취소
          </button>
          <button disabled={busy || selected.size === 0} onClick={submit} className={primaryBtn}>
            {busy
              ? "처리 중..."
              : mode === "promote"
                ? `관리자로 지정${selected.size ? ` (${selected.size})` : ""}`
                : `배정${selected.size ? ` (${selected.size})` : ""}`}
          </button>
        </>
      }
    >
      {error && <Banner tone="error">{error}</Banner>}
      <form
        className="relative"
        onSubmit={(e) => {
          e.preventDefault();
          setKeyword(keywordInput.trim());
        }}
      >
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]" />
        <input
          autoFocus
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          placeholder="이름, 이메일로 검색 후 Enter"
          className={`${inputClass} pl-9`}
        />
      </form>

      <div className="border border-[#EEEEEE] rounded-[10px] max-h-[280px] overflow-y-auto">
        {searching ? (
          <div className="py-10 text-center text-[13px] text-[#999]">검색 중...</div>
        ) : candidates.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-[#BBBBBB]">
            {keyword ? "검색 결과가 없습니다." : mode === "promote" ? "관리자로 지정할 수 있는 회원이 없습니다." : "옮길 수 있는 관리자가 없습니다."}
          </div>
        ) : (
          candidates.map((member) => {
            const checked = selected.has(member.id);
            return (
              <label
                key={member.id}
                className={`flex items-center gap-3 px-3 py-2.5 border-b border-[#F5F5F5] last:border-b-0 cursor-pointer transition-colors ${
                  checked ? "bg-[#F5F8FF]" : "hover:bg-[#FAFAFA]"
                }`}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(member)} className="w-4 h-4 accent-[#4285F4]" />
                <Avatar name={member.name} email={member.email} />
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-medium text-[#222] truncate">{member.name || "이름 없음"}</span>
                  <span className="block text-[12px] text-[#999] truncate">{member.email || "-"}</span>
                </span>
                {member.departmentName && <span className="shrink-0 text-[11px] text-[#999]">{member.departmentName}</span>}
              </label>
            );
          })
        )}
      </div>
      {!searching && candidates.length >= PICKER_PAGE_SIZE && (
        <p className="-mt-2 text-[12px] text-[#999]">최근 {PICKER_PAGE_SIZE}명만 보입니다. 찾는 사람이 없으면 검색해 주세요.</p>
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Array.from(selected.values()).map((m) => (
            <span key={m.id} className="inline-flex items-center gap-1 pl-2 pr-1 h-[26px] rounded-full bg-[#E8EFFF] text-[12px] text-[#3367D6]">
              {m.name || m.email}
              <button onClick={() => toggle(m)} aria-label={`${m.name || m.email} 선택 해제`} className="hover:text-[#222]">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {mode === "promote" && (
        <Field label="부서 (선택)" hint="고르지 않으면 회원의 지금 부서를 그대로 둡니다.">
          <DepartmentSelect departments={departments} value={departmentId} onChange={setDepartmentId} disabled={busy} emptyLabel="그대로 두기" />
        </Field>
      )}
    </DialogFrame>
  );
};

export default OrganizationManagement;
